import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Clock, FileText, Video, Palette, Sparkles, Upload, Check, ExternalLink,
  Eye, Heart, MessageCircle, Bookmark, Share2, Megaphone, Film, CheckCircle2,
  Camera, Users, Calendar, Plus, X, Link2,
} from "lucide-react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

// ============================================================
// ROLE-SPECIFIC DASHBOARDS
// ============================================================

const RoleDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/me")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Layout title="Cargando..." subtitle="Mi espacio">
        <div className="flex justify-center py-20"><span className="spinner" /></div>
      </Layout>
    );
  }

  const config = ROLE_CONFIG[user?.role] || ROLE_CONFIG.default;
  const Icon = config.icon;

  return (
    <Layout
      title={`Hola, ${user.name}`}
      subtitle={config.subtitle}
      actions={
        <Link to="/aura-ai" className="btn-outline flex items-center gap-2">
          <Sparkles size={14} /> Pregúntale a Aura
        </Link>
      }
    >
      {/* Hero stat */}
      <section className="mb-8 aura-card-flat p-6 md:p-8 flex items-start gap-5">
        <div className="w-14 h-14 rounded-2xl bg-[#D99D26]/15 text-[#8a5e10] flex items-center justify-center flex-shrink-0">
          <Icon size={24} />
        </div>
        <div className="flex-1">
          <div className="label-overline">{config.greeting}</div>
          <h2 className="font-display text-2xl font-medium mt-1 max-w-2xl">{config.intro}</h2>
        </div>
      </section>

      {/* Specific KPIs */}
      <KpisGrid data={data} role={user?.role} />

      {/* Main work area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <MyTasksPanel tasks={data?.tasks || []} role={user?.role} onChange={() => api.get("/dashboard/me").then((r) => setData(r.data))} />
        <MyBriefsPanel briefs={data?.briefs || []} role={user?.role} onChange={() => api.get("/dashboard/me").then((r) => setData(r.data))} />
      </div>

      {/* Bottom: posts metrics or calendar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <MyPostsPanel posts={data?.posts || []} role={user?.role} />
        <MyCalendarPanel items={data?.calendar || []} role={user?.role} />
      </div>
    </Layout>
  );
};

const ROLE_CONFIG = {
  creador_interno: {
    icon: Camera,
    subtitle: "Creadora interna · Producción de contenido",
    greeting: "Tu día creativo",
    intro: "Aquí están tus guiones, briefs y videos por grabar. Sube tus tomas y reporta el link cuando publiques.",
  },
  creador_ugc: {
    icon: Video,
    subtitle: "Creador UGC · Producción externa",
    greeting: "Tu brief de hoy",
    intro: "Lee el brief, graba según el shotlist y sube tus archivos. No revelas información sensible de la empresa.",
  },
  disenador: {
    icon: Palette,
    subtitle: "Diseño Gráfico · Briefs profesionales generados por IA",
    greeting: "Tu bandeja de diseño",
    intro: "Recibes briefs ya enriquecidos por Aura: medidas, formato, mensaje, paleta, CTA y referencias.",
  },
  filmmaker: {
    icon: Film,
    subtitle: "Filmmaker Comercial · Producción audiovisual",
    greeting: "Tu producción",
    intro: "Recibe guiones + shotlists. Registra locación, modelos, productos y entrega el corte final.",
  },
  filmmaker_ia: {
    icon: Sparkles,
    subtitle: "Filmmaker IA · Videos generados con inteligencia artificial",
    greeting: "Tus prompts y entregables",
    intro: "Recibes prompts, gráficos y promociones. Genera videos IA: recaps, animaciones, visuales para pauta.",
  },
  community_manager: {
    icon: Users,
    subtitle: "Community Manager · Programación y respuesta",
    greeting: "Tu pulso de comunidad",
    intro: "Programa publicaciones, monitorea comentarios virales y responde mensajes. Reporta hallazgos a Aura.",
  },
  default: {
    icon: CheckCircle2,
    subtitle: "Mi espacio creativo",
    greeting: "Bienvenido",
    intro: "Aquí están tus tareas, briefs y métricas personales.",
  },
};

const KpisGrid = ({ data, role }) => {
  const tasks = data?.tasks || [];
  const briefs = data?.briefs || [];
  const posts = data?.posts || [];
  const calendar = data?.calendar || [];

  const pendingTasks = tasks.filter((t) => t.status !== "terminada").length;
  const pendingBriefs = briefs.filter((b) => !["aprobado", "entregado", "publicado"].includes(b.status)).length;
  const totalViews = posts.reduce((s, p) => s + (p.views || 0), 0);
  const upcomingPosts = calendar.filter((c) => c.status !== "publicado" && c.status !== "medido").length;

  const tilesByRole = {
    creador_interno: [
      { label: "Guiones pendientes", value: briefs.filter(b => b.type === "ugc" || !b.type).length, sub: "Por grabar", icon: Film, accent: "yellow" },
      { label: "Tareas activas", value: pendingTasks, sub: "Tu pipeline", icon: Clock },
      { label: "Vistas acumuladas", value: totalViews.toLocaleString(), sub: `${posts.length} publicaciones`, icon: Eye, accent: "yellow" },
      { label: "Próximas publicaciones", value: upcomingPosts, sub: "En calendario", icon: Calendar },
    ],
    creador_ugc: [
      { label: "Briefs UGC", value: briefs.filter(b => b.type === "ugc").length, sub: "Asignados", icon: Video, accent: "yellow" },
      { label: "En espera de feedback", value: briefs.filter(b => b.status === "en_revision").length, sub: "Revisando", icon: Eye },
      { label: "Aprobados", value: briefs.filter(b => b.status === "aprobado").length, sub: "Entregables OK", icon: Check, accent: "yellow" },
      { label: "Total briefs", value: briefs.length, sub: "Histórico", icon: FileText },
    ],
    disenador: [
      { label: "Bandeja activa", value: briefs.filter(b => b.type === "diseno" && !["aprobado", "entregado", "publicado"].includes(b.status)).length, sub: "En cola", icon: Palette, accent: "yellow" },
      { label: "Urgentes", value: briefs.filter(b => b.type === "diseno" && b.priority === "urgente").length, sub: "Prioridad alta", icon: Clock },
      { label: "En revisión", value: briefs.filter(b => b.type === "diseno" && b.status === "en_revision").length, sub: "Esperando OK", icon: Eye },
      { label: "Entregados", value: briefs.filter(b => b.type === "diseno" && b.status === "entregado").length, sub: "Listos", icon: Check, accent: "yellow" },
    ],
    filmmaker: [
      { label: "Producciones activas", value: briefs.filter(b => b.type === "filmmaker" && b.status === "en_produccion").length, sub: "Grabando/editando", icon: Film, accent: "yellow" },
      { label: "Pendientes de grabar", value: briefs.filter(b => b.type === "filmmaker" && b.status === "en_espera").length, sub: "Por agendar", icon: Calendar },
      { label: "En revisión", value: briefs.filter(b => b.type === "filmmaker" && b.status === "en_revision").length, sub: "Esperando feedback", icon: Eye },
      { label: "Total producciones", value: briefs.filter(b => b.type === "filmmaker").length, sub: "Histórico", icon: CheckCircle2 },
    ],
    filmmaker_ia: [
      { label: "Videos IA activos", value: briefs.filter(b => b.type === "ia_video" && b.status !== "publicado").length, sub: "En generación", icon: Sparkles, accent: "yellow" },
      { label: "Prompts pendientes", value: briefs.filter(b => b.type === "ia_video" && b.status === "borrador").length, sub: "Por ejecutar", icon: FileText },
      { label: "Aprobados", value: briefs.filter(b => b.type === "ia_video" && b.status === "aprobado").length, sub: "OK", icon: Check },
      { label: "Publicados", value: briefs.filter(b => b.type === "ia_video" && b.status === "publicado").length, sub: "Online", icon: ExternalLink, accent: "yellow" },
    ],
    community_manager: [
      { label: "Publicaciones programadas", value: calendar.filter(c => c.status === "programado").length, sub: "En cola", icon: Calendar, accent: "yellow" },
      { label: "Por publicar hoy", value: calendar.filter(c => c.date?.startsWith(new Date().toISOString().split("T")[0])).length, sub: "Hoy", icon: Clock },
      { label: "Publicadas este mes", value: posts.length, sub: "Total", icon: CheckCircle2 },
      { label: "Engagement total", value: posts.reduce((s, p) => s + (p.likes || 0) + (p.comments || 0), 0).toLocaleString(), sub: "Likes + comentarios", icon: Heart, accent: "yellow" },
    ],
  };
  const tiles = tilesByRole[role] || tilesByRole.creador_interno;

  return (
    <section className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="role-kpis">
      {tiles.map((t, i) => {
        const Icon = t.icon;
        return (
          <div key={i} className="aura-card p-5 fade-up" style={{ animationDelay: `${i * 50}ms` }}>
            <div className="flex justify-between items-start">
              <div className="label-overline">{t.label}</div>
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${t.accent === "yellow" ? "bg-[#D99D26]/15 text-[#8a5e10]" : "bg-[#0A0A0A]/5 text-[#0A0A0A]"}`}>
                <Icon size={13} />
              </div>
            </div>
            <div className="mt-3 font-display text-3xl font-medium tracking-tighter">{t.value}</div>
            <div className="mt-1 text-xs text-[#666]">{t.sub}</div>
          </div>
        );
      })}
    </section>
  );
};

const MyTasksPanel = ({ tasks, role, onChange }) => {
  const visible = tasks.filter((t) => t.status !== "terminada").slice(0, 8);
  const updateStatus = async (id, status) => {
    await api.patch(`/tasks/${id}`, { status, completed_at: status === "terminada" ? new Date().toISOString() : null });
    onChange();
  };

  return (
    <div className="aura-card-flat p-6" data-testid="my-tasks">
      <div className="flex justify-between items-center mb-4">
        <div>
          <div className="label-overline">Mis tareas</div>
          <h2 className="font-display text-xl font-medium mt-1">Por hacer</h2>
        </div>
        <Link to="/tareas" className="text-xs font-medium text-[#8a5e10]">Ver todas</Link>
      </div>
      {visible.length === 0 ? (
        <div className="text-center py-8 text-sm text-[#666]">No tienes tareas pendientes. ¡Disfruta!</div>
      ) : (
        <div className="space-y-2">
          {visible.map((t) => (
            <div key={t.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-[#FAFAFA] transition-colors border border-[#F0F0F0]" data-testid={`my-task-${t.id}`}>
              <button
                onClick={() => updateStatus(t.id, "terminada")}
                className="w-5 h-5 rounded-full border-2 border-[#E5E5E5] hover:border-[#D99D26] mt-0.5 flex-shrink-0"
                title="Marcar terminada"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{t.title}</div>
                {t.description && <div className="text-xs text-[#666] line-clamp-1 mt-0.5">{t.description}</div>}
                <div className="flex gap-2 mt-2">
                  <span className={`pill ${t.priority === "urgente" ? "pill-red" : t.priority === "alta" ? "pill-yellow" : "pill-gray"} text-[10px]`}>{t.priority}</span>
                  <span className="pill pill-gray text-[10px]">{t.status.replace("_", " ")}</span>
                  {t.due_date && <span className="text-[11px] text-[#666]">📅 {new Date(t.due_date).toLocaleDateString("es-MX", { day: "numeric", month: "short" })}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const MyBriefsPanel = ({ briefs, role, onChange }) => {
  const [showSubmit, setShowSubmit] = useState(null);
  // Filter by role
  const filterByRole = {
    creador_interno: (b) => b.type === "ugc" || b.type === "filmmaker",
    creador_ugc: (b) => b.type === "ugc",
    disenador: (b) => b.type === "diseno",
    filmmaker: (b) => b.type === "filmmaker",
    filmmaker_ia: (b) => b.type === "ia_video",
    community_manager: () => true,
  };
  const filter = filterByRole[role] || (() => true);
  const visible = briefs.filter(filter).filter((b) => !["aprobado", "entregado", "publicado"].includes(b.status)).slice(0, 8);

  return (
    <div className="aura-card-flat p-6" data-testid="my-briefs">
      <div className="flex justify-between items-center mb-4">
        <div>
          <div className="label-overline">Mis briefs</div>
          <h2 className="font-display text-xl font-medium mt-1">Por producir</h2>
        </div>
        <Link to="/briefs" className="text-xs font-medium text-[#8a5e10]">Ver todos</Link>
      </div>
      {visible.length === 0 ? (
        <div className="text-center py-8 text-sm text-[#666]">Sin briefs asignados.</div>
      ) : (
        <div className="space-y-2">
          {visible.map((b) => (
            <div key={b.id} className="border border-[#F0F0F0] rounded-lg p-3 hover:border-[#D99D26]/30 transition-colors" data-testid={`my-brief-${b.id}`}>
              <div className="flex justify-between items-start mb-1">
                <div className="font-medium text-sm">{b.title}</div>
                <span className={`pill ${b.priority === "urgente" ? "pill-red" : b.priority === "alta" ? "pill-yellow" : "pill-gray"} text-[10px]`}>{b.priority}</span>
              </div>
              <div className="text-xs text-[#666]">{b.type} · {b.status.replace(/_/g, " ")}</div>
              <div className="flex gap-2 mt-2">
                <button onClick={() => setShowSubmit(b)} className="btn-ghost text-xs flex items-center gap-1" data-testid={`submit-${b.id}`}>
                  <Upload size={11} /> Subir entrega
                </button>
                <Link to="/briefs" className="text-xs text-[#8a5e10] hover:underline pt-1.5">Ver brief completo →</Link>
              </div>
            </div>
          ))}
        </div>
      )}
      {showSubmit && <SubmitForm brief={showSubmit} onClose={() => setShowSubmit(null)} onSaved={() => { setShowSubmit(null); onChange(); }} />}
    </div>
  );
};

const SubmitForm = ({ brief, onClose, onSaved }) => {
  const [link, setLink] = useState("");
  const [comment, setComment] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!link.trim() && !file) {
      toast.error("Sube un archivo o pega un link");
      return;
    }
    setSubmitting(true);
    try {
      const finalFiles = [...(brief.final_files || [])];
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", "ugc");
        fd.append("related_type", "brief");
        fd.append("related_id", brief.id);
        const res = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        finalFiles.push({ url: res.data.download_url, name: res.data.original_filename, type: "uploaded" });
      }
      if (link.trim()) {
        finalFiles.push({ url: link.trim(), name: "Link externo", type: "external" });
      }
      const comments = [...(brief.comments || []), { content: comment || "Entregable subido", role: "creator", created_at: new Date().toISOString() }];
      await api.patch(`/briefs/${brief.id}`, {
        final_files: finalFiles,
        status: "en_revision",
        comments,
      });
      toast.success("Entregable enviado para revisión");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-md p-7 space-y-4">
        <div className="flex justify-between items-start">
          <div>
            <div className="label-overline">Entrega</div>
            <h2 className="font-display text-xl font-medium">Subir trabajo</h2>
            <div className="text-xs text-[#666] mt-1">{brief.title}</div>
          </div>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div>
          <label className="aura-label">Archivo</label>
          <input type="file" onChange={(e) => setFile(e.target.files?.[0])} className="block w-full text-sm" data-testid="submit-file" />
        </div>
        <div className="text-center text-xs text-[#666] my-2">o</div>
        <div>
          <label className="aura-label">Link externo</label>
          <input className="aura-input" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://drive.google.com/..." data-testid="submit-link" />
        </div>
        <div>
          <label className="aura-label">Comentario (opcional)</label>
          <textarea className="aura-input" rows="2" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Notas para el revisor..." />
        </div>
        <button type="submit" disabled={submitting} className="btn-primary w-full justify-center flex items-center gap-2" data-testid="submit-btn">
          {submitting && <span className="spinner" />} Enviar entrega
        </button>
      </form>
    </div>
  );
};

const MyPostsPanel = ({ posts, role }) => {
  return (
    <div className="aura-card-flat p-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <div className="label-overline">Mi performance</div>
          <h2 className="font-display text-xl font-medium mt-1">Mis publicaciones</h2>
        </div>
        <Link to="/publicaciones" className="text-xs font-medium text-[#8a5e10]">Ver todas</Link>
      </div>
      {posts.length === 0 ? (
        <div className="text-center py-8 text-sm text-[#666]">Sin publicaciones registradas a tu nombre.</div>
      ) : (
        <div className="space-y-2">
          {posts.slice(0, 5).map((p) => (
            <div key={p.id} className="border-b border-[#F0F0F0] pb-2 last:border-0">
              <div className="flex justify-between items-start">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{p.title}</div>
                  <div className="text-xs text-[#666]">{p.platform} · {p.format}</div>
                </div>
                {p.post_link && <a href={p.post_link} target="_blank" rel="noopener noreferrer" className="text-[#666] hover:text-[#0A0A0A] ml-2"><ExternalLink size={13} /></a>}
              </div>
              <div className="flex gap-3 mt-2 text-xs text-[#666]">
                <span className="flex items-center gap-1"><Eye size={11} /> {(p.views || 0).toLocaleString()}</span>
                <span className="flex items-center gap-1"><Heart size={11} /> {p.likes || 0}</span>
                <span className="flex items-center gap-1"><MessageCircle size={11} /> {p.comments || 0}</span>
                <span className="flex items-center gap-1"><Bookmark size={11} /> {p.saves || 0}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const MyCalendarPanel = ({ items, role }) => {
  const upcoming = items
    .filter((i) => i.date && new Date(i.date) >= new Date(Date.now() - 86400000))
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""))
    .slice(0, 6);

  return (
    <div className="aura-card-flat p-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <div className="label-overline">Calendario</div>
          <h2 className="font-display text-xl font-medium mt-1">Mis próximas publicaciones</h2>
        </div>
        <Link to="/calendario" className="text-xs font-medium text-[#8a5e10]">Ver calendario</Link>
      </div>
      {upcoming.length === 0 ? (
        <div className="text-center py-8 text-sm text-[#666]">Sin publicaciones próximas.</div>
      ) : (
        <div className="space-y-2">
          {upcoming.map((c) => (
            <div key={c.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#FAFAFA] border-l-2" style={{ borderColor: c.campaign_color || "#D99D26" }}>
              <div className="text-center min-w-12">
                <div className="font-display text-lg font-medium">{new Date(c.date).getDate()}</div>
                <div className="text-[10px] uppercase tracking-wider text-[#666]">{new Date(c.date).toLocaleDateString("es-MX", { month: "short" })}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{c.title}</div>
                <div className="text-xs text-[#666]">{c.platform} · {c.format} · {c.time}</div>
              </div>
              <span className="pill pill-gray text-[10px]">{c.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RoleDashboard;
